/* Culinary Solvent article viewer. PhotoSwipe 5.4.4. */
(() => {
  'use strict';
  if (window.__csArticlePhotoSwipe) return;
  window.__csArticlePhotoSwipe = true;
  const ROOT = '.article__body[data-cs-photoswipe]';
  const MAX = 2560;
  const states = new Map();
  let session, opening = false;
  const modules = new Map();
  function url(value) {
    if (!value || /\{.*\}/.test(value)) return null;
    try { const u = new URL(value, document.baseURI); return /^https?:$/.test(u.protocol) ? u : null; }
    catch (_) { return null; }
  }
  function resizable(u) {
    const host = u.hostname === 'cdn.shopify.com' && /^\/s\/files\//.test(u.pathname);
    const local = u.origin === location.origin && /^\/cdn\/shop\/(files|products)\//.test(u.pathname);
    const transform = [...u.searchParams.keys()].some(k => /^(height|crop|pad|rect|size|scale)$/i.test(k));
    const legacy = /_(?:\d+x\d*|x\d+|small|medium|large|grande|compact|master)(?:_crop_\w+)?(?:@\d+x)?\.[^.]+$/i.test(u.pathname);
    return (host || local) && !transform && !legacy && /\.(jpe?g|png|webp|avif)$/i.test(u.pathname);
  }
  function sized(u, w) { const copy = new URL(u); copy.searchParams.set('width', String(w)); return copy.href; }
  function source(img) {
    const explicit = url(img.dataset.pswpSrc);
    if (explicit) return explicit;
    const lazy = url(img.getAttribute('data-src')?.replace(/\{width\}/g, String(MAX)));
    if (lazy) return lazy;
    const set = img.getAttribute('data-srcset') || img.getAttribute('srcset');
    if (set && !set.includes('data:')) {
      const items = set.split(',').map(part => {
        const m = part.trim().match(/^(\S+)\s+(\d+)w$/);
        return m ? { u: url(m[1]), w: Number(m[2]) } : null;
      }).filter(item => item?.u).sort((a, b) => b.w - a.w);
      if (items.length) return items[0].u;
    }
    return url(img.currentSrc) || url(img.getAttribute('src'));
  }
  function eligible(img, root) {
    const picture = img.closest('picture');
    return img.closest(ROOT) === root &&
      !img.closest('a,button,[role="button"],input,select,textarea,.article__featured-image,[data-cs-pswp-exclude]') &&
      img.dataset.csPswp !== 'off' && !picture?.querySelector('source[media]') && Boolean(source(img));
  }
  function caption(img, root) {
    const figure = img.closest('figure');
    const node = figure && root.contains(figure) ? figure.querySelector('figcaption') : null;
    return (img.dataset.pswpCaption || node?.textContent || img.alt || '').trim();
  }
  function measure(src, signal) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      let timer;
      const cleanup = () => {
        clearTimeout(timer); image.onload = null; image.onerror = null;
        signal.removeEventListener('abort', abort);
      };
      const fail = () => { cleanup(); image.removeAttribute('src'); reject(new Error('Image unavailable.')); };
      const abort = () => fail();
      if (signal.aborted) { fail(); return; }
      signal.addEventListener('abort', abort, { once: true });
      timer = setTimeout(fail, 20000);
      image.onload = () => {
        const width = image.naturalWidth, height = image.naturalHeight;
        cleanup();
        if (width > 0 && height > 0) resolve({ width, height }); else reject(new Error('Invalid image dimensions.'));
      };
      image.onerror = fail;
      image.decoding = 'async';
      image.src = src;
    });
  }
  async function item(img, root, signal) {
    const u = source(img);
    if (!u) throw new Error('No image source.');
    const resize = resizable(u);
    let src = resize ? sized(u, MAX) : u.href;
    const w = Number(img.dataset.pswpWidth), h = Number(img.dataset.pswpHeight);
    // Explicit metadata must describe data-pswp-src. Inline HTML dimensions may be display dimensions.
    const known = Boolean(img.dataset.pswpSrc && Number.isInteger(w) && Number.isInteger(h) && w > 0 && h > 0);
    const dimensions = known ? {
      width: resize ? Math.min(w, MAX) : w,
      height: resize ? Math.max(1, Math.round(h * Math.min(w, MAX) / w)) : h
    } : await measure(src, signal);
    if (resize) src = sized(u, dimensions.width);
    const data = { type: 'image', src, ...dimensions, alt: img.alt || '', caption: caption(img, root), element: img };
    if (resize) {
      const widths = [...new Set([320,640,960,1280,1600,2048,dimensions.width]
        .filter(n => n <= dimensions.width))].sort((a,b) => a-b);
      data.srcset = widths.map(n => `${sized(u,n)} ${n}w`).join(', ');
    }
    return data;
  }
  function css(href) {
    const u = url(href);
    if (!u) return Promise.reject(new Error('Missing PhotoSwipe CSS URL.'));
    const existing = [...document.querySelectorAll('link[data-cs-pswp-css]')].find(link => link.href === u.href);
    if (existing?.sheet) return Promise.resolve();
    return new Promise((resolve,reject) => {
      const link = existing || document.createElement('link');
      let timer;
      const finish = error => {
        clearTimeout(timer); link.removeEventListener('load',loaded); link.removeEventListener('error',failed);
        if (error) { link.remove(); reject(error); } else resolve();
      };
      const loaded = () => finish();
      const failed = () => finish(new Error('PhotoSwipe CSS unavailable.'));
      link.addEventListener('load',loaded,{once:true}); link.addEventListener('error',failed,{once:true});
      timer = setTimeout(failed,15000);
      if (!existing) { link.rel='stylesheet'; link.href=u.href; link.dataset.csPswpCss=''; document.head.append(link); }
    });
  }
  async function open(state,img,button) {
    if (opening || session || !state.root.isConnected) return;
    const images = [...state.root.querySelectorAll('img')].filter(i => eligible(i,state.root));
    const index = images.indexOf(img);
    if (index < 0) return;
    opening=true; button.setAttribute('aria-busy','true'); state.status.textContent='Opening image viewer…';
    try {
      const u = url(state.root.dataset.csPswpModule);
      if (!u) throw new Error('Missing PhotoSwipe module URL.');
      if (!modules.has(u.href)) modules.set(u.href,import(u.href).catch(error => { modules.delete(u.href); throw error; }));
      const [module] = await Promise.all([modules.get(u.href),css(state.root.dataset.csPswpCss)]);
      if (!state.root.isConnected || states.get(state.root) !== state) return;
      const controller = new AbortController();
      const dataSource = images.map(() => ({ type:'html',html:'<div class="cs-pswp-message" role="status">Loading image…</div>' }));
      const pending = new Set();
      const viewer = new module.default({
        dataSource,index,mainClass:'cs-article-pswp',showHideAnimationType:'none',preload:[0,0],loop:false,
        bgOpacity:.94,returnFocus:false,trapFocus:true,initialZoomLevel:'fit',
        secondaryZoomLevel: levels => Math.max(levels.fit,Math.min(1,levels.fit*2)),
        maxZoomLevel: levels => Math.max(levels.fit,1),
        paddingFn: () => ({top:64,bottom:136,left:12,right:12}),
        errorMsg:'This image could not be loaded.'
      });
      session={viewer,state,controller};
      let captionElement;
      const updateCaption = () => {
        if (!captionElement) return;
        captionElement.textContent=dataSource[viewer.currIndex]?.caption || '';
        captionElement.hidden=!captionElement.textContent;
      };
      const resolveActive = () => {
        const n=viewer.currIndex;
        if (dataSource[n].type==='image' || pending.has(n)) return;
        pending.add(n);
        item(images[n],state.root,controller.signal).then(data => {
          if (controller.signal.aborted) return;
          dataSource[n]=data; viewer.refreshSlideContent(n); updateCaption();
        }).catch(() => {
          if (controller.signal.aborted) return;
          dataSource[n]={type:'html',html:'<div class="cs-pswp-message" role="status">Unable to load this image. Close the viewer and try again.</div>'};
          viewer.refreshSlideContent(n);
        });
      };
      viewer.on('uiRegister',() => viewer.ui.registerElement({
        name:'cs-caption',className:'cs-pswp-caption',order:9,isButton:false,appendTo:'root',
        onInit: element => { captionElement=element; updateCaption(); }
      }));
      viewer.on('change',() => { updateCaption(); resolveActive(); });
      viewer.on('destroy',() => {
        controller.abort(); if (session?.viewer===viewer) session=null;
        if (button.isConnected) button.focus({preventScroll:true});
      });
      viewer.init(); resolveActive(); state.status.textContent='';
    } catch (error) {
      if (session?.state===state) { session.controller.abort(); session.viewer.destroy(); session=null; }
      state.status.textContent='Image viewer unavailable. Please try again.';
      console.warn('[Article image viewer]',error);
    } finally { opening=false; button.removeAttribute('aria-busy'); }
  }
  function mount(root) {
    if (states.has(root)) return;
    const status=document.createElement('span'); status.className='cs-pswp-status'; status.setAttribute('role','status'); root.append(status);
    const state={root,status,buttons:new Map()};
    const scan=() => {
      for (const [img,button] of state.buttons) if (!root.contains(img) || !eligible(img,root)) {
        button.remove(); img.classList.remove('cs-pswp-image'); state.buttons.delete(img);
      }
      root.querySelectorAll('img').forEach(img => {
        if (!eligible(img,root) || state.buttons.has(img)) return;
        const button=document.createElement('button'); button.type='button'; button.className='cs-pswp-trigger'; button.textContent='Enlarge image';
        button.setAttribute('aria-label',img.alt ? `Enlarge image: ${img.alt}` : 'Enlarge image');
        (img.closest('picture') || img).after(button); img.classList.add('cs-pswp-image'); state.buttons.set(img,button);
      });
    };
    state.click=event => {
      if (event.defaultPrevented || event.button!==0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
      const img=event.target.closest('img');
      if (img && state.buttons.has(img) && eligible(img,root)) { event.preventDefault(); open(state,img,state.buttons.get(img)); return; }
      const button=event.target.closest('.cs-pswp-trigger');
      if (button) for (const [image,control] of state.buttons) if (button===control) { event.preventDefault(); open(state,image,button); break; }
    };
    state.observer=new MutationObserver(scan); states.set(root,state); root.addEventListener('click',state.click); scan();
    state.observer.observe(root,{childList:true,subtree:true,attributes:true,
      attributeFilter:['src','srcset','data-src','data-srcset','href','data-cs-pswp','data-cs-pswp-exclude','data-pswp-src','role']});
  }
  function unmount(root) {
    const state=states.get(root); if (!state) return;
    state.observer.disconnect(); root.removeEventListener('click',state.click);
    if (session?.state===state) session.viewer.destroy();
    for (const [img,button] of state.buttons) { button.remove(); img.classList.remove('cs-pswp-image'); }
    statusRemove(state); states.delete(root);
  }
  function statusRemove(state) { state.status.remove(); }
  const start=() => document.querySelectorAll(ROOT).forEach(mount);
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',start,{once:true}); else start();
  document.addEventListener('shopify:section:load',event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches(ROOT)) mount(event.target); event.target.querySelectorAll(ROOT).forEach(mount);
  });
  document.addEventListener('shopify:section:unload',event => {
    if (!(event.target instanceof Element)) return;
    for (const root of states.keys()) if (event.target===root || event.target.contains(root)) unmount(root);
  });
})();
